<script setup>
import { ref, onMounted } from 'vue';

// 1. Accept user object as a prop
const props = defineProps({
  user: {
    type: Object,
    required: true,
    default: () => ({})
  }
});

// 2. Define emit for logout action
const emit = defineEmits(['logout']);

// Cookie Helper Functions
const COOKIE_NAME = 'user_card_minimised';

const setCookie = (name, value, days = 30) => {
  const date = new Date();
  date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
  document.cookie = `${name}=${value}; expires=${date.toUTCString()}; path=/`;
};

const getCookie = (name) => {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]*)'));
  return match ? match[2] : null;
};

// 3. Local minimised toggle state & animation state
const isMinimised = ref(false);
const isAnimating = ref(false);

// Read saved state when component loads
onMounted(() => {
  const savedState = getCookie(COOKIE_NAME);
  if (savedState !== null) {
    isMinimised.value = savedState === 'true';
  }
});

const toggleMinimise = () => {
  if (isAnimating.value) return; // Prevent spam clicking during animation

  isAnimating.value = true;

  // Mid-way point: change layout structure when card is fully shrunk
  setTimeout(() => {
    isMinimised.value = !isMinimised.value;
    // Save state to cookie (valid for 30 days)
    setCookie(COOKIE_NAME, isMinimised.value ? 'true' : 'false', 30);
  }, 150);

  // Reset animation class when complete
  setTimeout(() => {
    isAnimating.value = false;
  }, 300);
};

const onLogoutClick = () => {
  emit('logout');
};
</script>

<template>
  <div class="card-user-container" :class="{ 'is-minimised': isMinimised, 'is-morphing': isAnimating }">
    <button type="button" class="toggle-btn" :title="isMinimised ? 'Expand Card' : 'Minimise Card'"
      @click="toggleMinimise">
      <i class="fa fa-chevron-up icon-chevron"></i>
    </button>

    <!-- User Avatar Image -->
    <div class="card-user-avatar">
      <img :src="user.picture" alt="User avatar" class="user-image" />
    </div>

    <!-- User Info (Hidden when minimised) -->
    <div v-if="!isMinimised" class="card-user-bio">
      <h4>{{ user.firstname || user.meta_firstname }} {{ user.lastname || user.meta_lastname }}</h4>
      <p class="role">{{ user.role || user.meta_role }}</p>

      <span v-if="user.meta_location" class="location">
        <span class="location-icon fa fa-map-marker"></span>
        <span v-if="user.meta_location?.address_line1" class="location-text">
          {{ user.meta_location.address_line1 }}
        </span>
        <span v-if="user.meta_location?.address_line2" class="location-text">
          {{ user.meta_location.address_line2 }}
        </span>
        <span v-if="user.meta_location?.address_line3" class="location-text">
          {{ user.meta_location.address_line3 }}
        </span>
        <span v-if="user.meta_location?.postcode" class="location-text">
          {{ user.meta_location.postcode }}
        </span>
      </span>
    </div>

    <!-- Logout Button -->
    <div class="card-user-button">
      <a class="btn" @click="onLogoutClick">LOGOUT</a>
      <span>{{ user.meta_firstname }}</span>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use "@/assets/styles/abstract/variables" as *;
// SCSS Variables
$primary-text: #0f172a;
$muted-icon: #64748b;
$accent-brand: #9F1757;

/* Base Container */
.card-user-container {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1.5rem;
  transform-origin: center;
  will-change: transform, opacity;

  /* Toggle Button & Chevron */
  .toggle-btn {
    position: absolute;
    top: 10px;
    right: 10px;
    display: flex;
    background: transparent;
    border: none;
    color: $muted-icon;
    cursor: pointer;
    padding: 4px 8px;
    font-size: 0.85rem;
    border-radius: 4px;
    z-index: 2;

    &:hover {
      color: $primary-text;
    }
  }

  .icon-chevron {
    transition: transform 0.3s ease;
  }

  /* User Button Span */
  .card-user-button span {
    display: none;
    color: $accent-brand;
    font-size: 25px;
  }

  /* Minimised Horizontal Layout State */
  &.is-minimised {
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    gap: 1rem;
    padding: 0.75rem 2.5rem 0.75rem 1rem;

    .icon-chevron {
      transform: rotate(180deg);
    }

    .card-user-avatar,
    .card-user-button {
      margin: 0;
    }

    .card-user-avatar img {
      width: 70px;
      height: 70px;
      padding: 0;
      margin-left: 15px;
    }

    .card-user-button {
      .btn {
        display: none;
      }

      span {
        display: block;
      }
    }

    .toggle-btn {
      top: auto;
    }
  }

  .card-user-bio {
    h4 {
      color: $dark-pink;
      font-weight: 1000;
    }

    .role {
      color: $orange;
      font-size: 1.25rem;
    }

    .location-icon {
      color: $dark-blue;
      font-size: 1.2rem;
    }

    span {
      color: $dark-blue;
      font-size: 1.2rem;
    }
  }

  /* Morph Animation State */
  &.is-morphing {
    animation: cardMorph 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
}

/* Keyframes */
@keyframes cardMorph {
  0% {
    opacity: 1;
    transform: scale(1);
  }

  50% {
    opacity: 0.2;
    transform: scale(0.92);
  }

  100% {
    opacity: 1;
    transform: scale(1);
  }
}
</style>